const WINDOW = typeof window === "object" && window || void 0;
const defaultBreakpoints = {
  xmobile: 323,
  mobile: 619,
  tablet: 991,
  desktop: 992,
  widescreen: 1280,
  hd: 1920
};
class Devices {
  static breakpoints = defaultBreakpoints;
  static withSizes(breakpoints) {
    this.breakpoints = Object.assign(this.breakpoints, breakpoints);
    return this;
  }
  /**
   * is width xmobile and below
   */
  static isXMobile() {
    const width = WINDOW?.innerWidth || 1e3;
    return width <= this.breakpoints.xmobile;
  }
  /**
   * is width mobile and below. returns true even on xmobile
   */
  static isMobile() {
    const width = WINDOW?.innerWidth || 1e3;
    return width <= this.breakpoints.mobile;
  }
  /**
   * is width above mobile and below desktop
   */
  static isTabletOnly() {
    const width = WINDOW?.innerWidth || 1e3;
    return width <= this.breakpoints.tablet && width > this.breakpoints.mobile;
  }
  /**
   * is width less than tablet? returns true even on mobile and xmobile
   */
  static isTablet() {
    const width = WINDOW?.innerWidth || 1e3;
    return width <= this.breakpoints.tablet;
  }
  /**
   * is width greater than tablet?
   */
  static isDesktop() {
    const width = WINDOW?.innerWidth || 1e3;
    return width >= this.breakpoints.tablet;
  }
  /**
   * is width greater than max-flex of desktop (1280px). i.e the breakpoint after which
   * max-width does not keep growing.
   */
  static isWidescreen() {
    const width = WINDOW?.innerWidth || 0;
    return width >= this.breakpoints.widescreen;
  }
  /**
   * is width greater than or equal to HD.
   */
  static isHD() {
    const width = WINDOW?.innerWidth || 0;
    return width >= this.breakpoints.hd;
  }
}

const propertyMapFull = {
  Desktop: "properties",
  Laptop: "laptopProperties",
  Tablet: "tabletProperties",
  Mobile: "mobileProperties"
};
const reversePropertyMapFull = {
  properties: "Desktop",
  laptopProperties: "Laptop",
  tabletProperties: "Tablet",
  mobileProperties: "Mobile"
};
const propertyMap = {
  Laptop: "laptopProperties",
  Tablet: "tabletProperties",
  Mobile: "mobileProperties"
};
const completeProperties = {
  properties: 12,
  laptopProperties: 12,
  tabletProperties: 8,
  mobileProperties: 4
};
const leftThreshold = 200;
const rightThreshold = 200;
const horizontalGutterGaps = {
  properties: 32,
  laptopProperties: 16,
  tabletProperties: 16,
  mobileProperties: 16
};
const verticalGutterGaps = {
  properties: 32,
  laptopProperties: 16,
  tabletProperties: 16,
  mobileProperties: 16
};
const changeContent = (obj, value, target) => {
  if (target === obj.id) {
    obj.content = value;
  } else {
    Object.values(obj).reduce((acc, val) => {
      if (acc !== void 0)
        return acc;
      if (typeof val === "object")
        return changeContent(val, value, target);
      return acc;
    }, void 0);
  }
};
const boxHasChild = (child) => {
  if (typeof child !== "string" && child && child.length) {
    if (child[0].children && child[0].children.length) {
      return false;
    }
  }
  return true;
};
const checkSectionEnabled = (masterJson, selectedAccordionPropName, side, width) => {
  let section;
  if (side === "left") {
    section = "leftSpan";
  } else if (side === "right") {
    section = "rightSpan";
  } else {
    return false;
  }
  if (masterJson && masterJson[selectedAccordionPropName] && masterJson[selectedAccordionPropName][section]) {
    const widthValue = masterJson[selectedAccordionPropName][section];
    if (widthValue && widthValue > width) {
      return true;
    }
  }
  return false;
};
const mergeExistingProperties = (element, propName) => {
  return element[propName] ? { ...element.properties, ...element[propName] } : element.properties;
};
const addElementData = (columnLength, type, obj, selectedNode) => {
  moveAllToBack(obj);
  const spanLength = 4;
  const selectedId = selectedNode.id;
  if (selectedId) {
    if (selectedId === obj.id) {
      const newObj = getNewObjectData(
        type,
        selectedNode,
        spanLength,
        columnLength
      );
      if (obj.children) {
        if (typeof obj.children === "string") {
          obj.children = [newObj];
        } else {
          obj.children.push(newObj);
        }
      } else {
        obj.children = [newObj];
      }
      if (newObj.type === "Grid") {
        addElementData(columnLength, "Box", obj, newObj);
      }
    } else {
      Object.values(obj).reduce((acc, val) => {
        if (acc !== void 0)
          return acc;
        if (typeof val === "object")
          return addElementData(columnLength, type, val, selectedNode);
        return acc;
      }, void 0);
    }
  }
};
const getNewObjectData = (type, selectedNode, spanLength, columnLength) => {
  let newBackground = "";
  if (type === "Grid") {
    newBackground = "lightblue";
  } else {
    newBackground = "#E1EDF6";
  }
  const defaultProperties = type === "Grid" ? {
    verticalGap: 0,
    horizontalGap: 0,
    column: 0,
    height: 0,
    width: 0,
    background: ""
  } : {
    span: 0,
    row: 0,
    columnStart: 0,
    columnEnd: 0,
    spanStartEnd: "",
    width: 0,
    height: 0,
    background: ""
  };
  const newObj = {
    id: Math.floor(Math.random() * 1e5 + 1),
    type,
    isActive: false,
    component: "div",
    content: "text",
    properties: { ...defaultProperties },
    laptopProperties: { ...defaultProperties },
    tabletProperties: { ...defaultProperties },
    mobileProperties: { ...defaultProperties },
    children: ""
  };
  if (type === "Grid") {
    const grids = document.getElementsByClassName("grid");
    const gridLength = grids.length + 1;
    newObj.content = "Grid " + gridLength;
    Object.keys(completeProperties).forEach((propName) => {
      const castedPropName = propName;
      const verticalGap = verticalGutterGaps[castedPropName];
      const horizontalGap = horizontalGutterGaps[castedPropName];
      newObj[castedPropName].verticalGap = verticalGap;
      newObj[castedPropName].horizontalGap = horizontalGap;
      newObj[castedPropName].column = columnLength;
      newObj[castedPropName].background = newBackground;
      newObj[castedPropName].width = selectedNode[castedPropName].width;
      newObj[castedPropName].height = selectedNode[castedPropName].height;
    });
  } else {
    const boxes = document.getElementsByClassName("box");
    const boxLength = boxes.length + 1;
    newObj.component = "ResizableBox";
    newObj.clicked = true;
    newObj.content = "Box " + boxLength;
    Object.keys(completeProperties).forEach(function(propName) {
      const castedPropName = propName;
      const horizontalGap = findGap(
        "horizontalGap",
        selectedNode,
        castedPropName
      );
      const parentGridLength = selectedNode[castedPropName].column ? selectedNode[castedPropName].column : completeProperties[castedPropName];
      let totalSpan = spanLength;
      newObj[castedPropName].background = newBackground;
      newObj[castedPropName].span = spanLength;
      newObj[castedPropName].columnStart = 1;
      newObj[castedPropName].columnEnd = 1 + spanLength;
      newObj[castedPropName].row = 1;
      newObj[castedPropName].height = selectedNode[castedPropName].height;
      if (selectedNode.children?.length) {
        const lastElementHeight = selectedNode.children[selectedNode.children?.length - 1][castedPropName].height;
        if (lastElementHeight) {
          newObj[castedPropName].height = lastElementHeight;
        }
      }
      if (selectedNode.id === "wrapper" && !selectedNode.children) {
        newObj[castedPropName].height = 100;
      }
      newObj[castedPropName].spanStartEnd = `${newObj[castedPropName].columnStart}-${newObj[castedPropName].columnEnd - 1}`;
      if (selectedNode.children && typeof selectedNode.children !== "string") {
        const result = selectedNode.children.map(
          (a) => a[castedPropName]
        );
        let rows = [
          ...new Set(result.map((a) => a.row))
        ].sort();
        const max = Math.max(...rows);
        rows = Array.from({ length: max }, (_, i) => i + 1);
        let spanAvailable = false;
        let initial = false;
        rows.forEach((rowValue) => {
          const spans = [
            ...new Set(
              result.filter((spa) => spa.row === rowValue).map((a) => {
                if (a.spanStartEnd.includes("-")) {
                  return a.spanStartEnd;
                }
                return `${a.columnStart}-${a.columnEnd - 1}`;
              })
            )
          ];
          spans.sort(function(a, b) {
            return parseInt(a.split("-")[0]) - parseInt(b.split("-")[0]);
          });
          spans.forEach((span, index) => {
            let availableSpan = 0;
            if (!spans[index + 1]) {
              availableSpan = parentGridLength - parseInt(span.split("-")[1]);
            }
            if (spans[index + 1] && typeof spans[index + 1] === "string") {
              availableSpan = parseInt(spans[index + 1].split("-")[0]) - 1 - parseInt(span.split("-")[1]);
            }
            if (!spans[index - 1]) {
              if (parseInt(span.split("-")[0]) - 1 >= 4) {
                initial = true;
                availableSpan = parseInt(span.split("-")[0]) - 1;
              }
            }
            if (availableSpan >= 4) {
              if (spanAvailable) {
                return;
              }
              spanAvailable = true;
              newObj[castedPropName].row = rowValue;
              if (!initial) {
                newObj[castedPropName].columnStart = parseInt(span.split("-")[1]) + 1;
                newObj[castedPropName].columnEnd = newObj[castedPropName].columnStart + 4;
                newObj[castedPropName].spanStartEnd = `${newObj[castedPropName].columnStart}-${newObj[castedPropName].columnEnd - 1}`;
              }
            }
          });
          if (spans.length === 0) {
            if (spanAvailable) {
              return;
            }
            spanAvailable = true;
            newObj[castedPropName].row = rowValue;
          }
        });
        if (!spanAvailable) {
          selectedNode.children.forEach((element) => {
            totalSpan += parseInt(
              element[castedPropName].span.toString()
            );
          });
          const result2 = selectedNode.children.map(
            (a) => a[castedPropName]
          );
          const rows2 = [
            ...new Set(result2.map((a) => a.row))
          ].sort();
          const max2 = Math.max(...rows2);
          newObj[castedPropName].row = max2;
          const spans = [
            ...new Set(
              result2.filter(
                (spa) => spa.row === Math.ceil(totalSpan / parentGridLength)
              ).map((a) => a.spanStartEnd)
            )
          ];
          spans.sort(function(a, b) {
            return parseInt(a.split("-")[0]) - parseInt(b.split("-")[0]);
          });
          if (spans.length && parseInt(spans[spans.length - 1].split("-")[1]) + 4 <= parentGridLength) {
            newObj[castedPropName].columnStart = selectedNode.children[selectedNode.children.length - 1][castedPropName].columnEnd;
            newObj[castedPropName].columnEnd = newObj[castedPropName].columnStart + newObj[castedPropName].span;
            newObj[castedPropName].spanStartEnd = `${newObj[castedPropName].columnStart}-${newObj[castedPropName].columnEnd - 1}`;
          } else {
            newObj[castedPropName].row = newObj[castedPropName].row + 1;
          }
        }
      }
      const parentWidth = selectedNode[castedPropName]?.width;
      const totalGap = horizontalGap * (parentGridLength - 1);
      const singleColumnWidth = (parentWidth - totalGap) / parentGridLength;
      const width = 4 * singleColumnWidth + horizontalGap * 3;
      newObj[castedPropName].width = width;
    });
  }
  return newObj;
};
const moveAllToBack = (jsonData) => {
  if (jsonData.id) {
    jsonData.clicked = false;
  }
  if (jsonData.children && typeof jsonData.children !== "string") {
    jsonData.children.forEach((element) => {
      moveAllToBack(element);
    });
  }
};
const reOrderData = (jsonData, device) => {
  const propName = propertyMapFull[device];
  if (jsonData.children && typeof jsonData.children !== "string") {
    jsonData.children.sort(
      (a, b) => a[propName].row - b[propName].row
    );
    let difference = 0;
    if (jsonData.children && typeof jsonData.children !== "string") {
      const firstChildRow = jsonData.children[0][propName].row;
      if (firstChildRow !== 1) {
        difference = 1 - firstChildRow;
      }
      jsonData.children.forEach((element) => {
        element[propName].row = element[propName].row + difference;
      });
    }
  }
};
const findParentGrid = (obj, selectedId) => {
  if (selectedId === obj.id) {
    return obj;
  }
  Object.values(obj).reduce((acc, val) => {
    if (acc !== void 0)
      return acc;
    if (typeof val === "object")
      return findParentGrid(val, selectedId);
    return acc;
  }, void 0);
};
const findGridHeight = (jsonData, columnId, selectedAccordionPropName, isAddNewEnabled) => {
  let maxHeight = 0;
  const parent = findParentGrid(jsonData, columnId);
  const horizontalGap = findGap(
    "horizontalGap",
    parent || jsonData,
    selectedAccordionPropName
  );
  const verticalGap = findGap(
    "verticalGap",
    parent || jsonData,
    selectedAccordionPropName
  );
  if (parent && parent.children && typeof parent.children !== "string") {
    if (parent.type === "Box") {
      const childGrid = parent.children[0];
      if (isAddNewEnabled && childGrid.children && typeof childGrid.children !== "string") {
        const totalColumnLength = childGrid[selectedAccordionPropName].column;
        const result = childGrid.children.map(
          (a) => a[selectedAccordionPropName]
        );
        const rows = [
          ...new Set(result.map((a) => a.row))
        ].sort(function(a, b) {
          return a - b;
        });
        const lastRow = Math.max(...rows);
        const elementsInRow = childGrid.children.filter(
          (obj) => obj[selectedAccordionPropName].row === lastRow
        );
        const totalSpanUsed = elementsInRow.length ? elementsInRow.map(
          (item) => item[selectedAccordionPropName].span
        ).reduce((prev, next) => prev + next) : 0;
        if (totalSpanUsed + 4 > totalColumnLength) {
          maxHeight = parent.children[0][selectedAccordionPropName]?.height + horizontalGap + childGrid.children[childGrid.children.length - 1][selectedAccordionPropName]?.height;
        }
      }
      if (!maxHeight) {
        maxHeight = parent.children[0][selectedAccordionPropName]?.height;
      }
    } else {
      const result = parent.children.map(
        (a) => a[selectedAccordionPropName]
      );
      let rows = [...new Set(result.map((a) => a.row))].sort();
      const max = Math.max(...rows);
      rows = Array.from({ length: max }, (_, i) => i + 1);
      const fullHeights = [];
      rows.forEach((rowValue) => {
        const heights = [
          ...new Set(
            result.filter((spa) => spa.row === rowValue).map((a) => {
              return a.height ? a.height : 100;
            })
          )
        ];
        const maxHeightOfRow = Math.max(...heights);
        fullHeights.push(maxHeightOfRow);
      });
      fullHeights.forEach((element, index) => {
        maxHeight += element;
        if (index > 0) {
          maxHeight += verticalGap;
        }
      });
      if (parent[selectedAccordionPropName].paddingTop) {
        maxHeight += parent[selectedAccordionPropName].paddingTop || 0;
      }
      if (parent[selectedAccordionPropName].paddingBottom) {
        maxHeight += parent[selectedAccordionPropName].paddingBottom || 0;
      }
    }
  }
  return maxHeight || 100;
};
const setParentHeight = (jsonData, isAddNewEnabled) => {
  if (jsonData.children && typeof jsonData.children !== "string") {
    jsonData.properties.height = findGridHeight(
      jsonData,
      jsonData.id,
      "properties",
      isAddNewEnabled
    );
    jsonData.children.forEach((element) => {
      setParentHeight(element, isAddNewEnabled);
    });
  } else {
    jsonData.properties.height = jsonData.properties.height ? jsonData.properties.height : 100;
  }
};
const getConsecutiveSequences = (arr, sequenceLength) => {
  const result = [];
  for (let i = 0; i < arr.length - sequenceLength + 1; i++) {
    let isConsecutive = true;
    for (let j = i + 1; j < i + sequenceLength; j++) {
      if (arr[j] !== arr[j - 1] + 1) {
        isConsecutive = false;
        break;
      }
    }
    if (isConsecutive) {
      result.push(arr.slice(i, i + sequenceLength));
    }
  }
  return result;
};
const findOccupiedSpaces = (childInRow, selectedAccordionPropName) => {
  const occupiedSpaces = [];
  childInRow.forEach((element) => {
    for (let index = element[selectedAccordionPropName].columnStart; index < element[selectedAccordionPropName].columnEnd; index++) {
      if (index) {
        occupiedSpaces.push(index);
      }
    }
  });
  return occupiedSpaces;
};
const findOverlappedElement = (objectArray, selectedAccordionPropName, start, end) => {
  const resultArray = [];
  for (let i = 0; i < objectArray.length; i++) {
    const object = objectArray[i];
    const objectStart = object[selectedAccordionPropName].columnStart;
    const objectEnd = object[selectedAccordionPropName].columnEnd - 1;
    if (objectStart <= end && objectEnd >= start) {
      resultArray.push(object);
    }
  }
  return resultArray;
};
const findChild = (id, obj) => {
  if (obj.id === id) {
    return obj;
  }
  if (obj.children && typeof obj.children !== "string") {
    for (let i = 0; i < obj.children.length; i++) {
      const child = obj.children[i];
      const result = findChild(id, child);
      if (result) {
        return result;
      }
    }
  }
  return null;
};
const setChildProps = (currentElement, selectedAccordionValue) => {
  if (selectedAccordionValue === "Laptop") {
    currentElement.laptopProperties = mergeExistingProperties(
      currentElement,
      "laptopProperties"
    );
  } else if (selectedAccordionValue === "Tablet") {
    currentElement.tabletProperties = mergeExistingProperties(
      currentElement,
      "tabletProperties"
    );
  } else if (selectedAccordionValue === "Mobile") {
    currentElement.mobileProperties = mergeExistingProperties(
      currentElement,
      "mobileProperties"
    );
  }
};
const findBoxWidth = (parent, parentWidth, columLen, span, selectedAccordionPropName) => {
  const horizontalGap = findGap(
    "horizontalGap",
    parent,
    selectedAccordionPropName
  );
  const totalGap = horizontalGap * (columLen - 1);
  const singleColumnWidth = (parentWidth - totalGap) / columLen;
  const width = span * singleColumnWidth + horizontalGap * (span - 1);
  return width;
};
const findGap = (position, element, selectedAccordionPropName) => {
  if (element[selectedAccordionPropName][position] || element[selectedAccordionPropName][position] === 0) {
    return element[selectedAccordionPropName][position];
  }
  if (position === "horizontalGap") {
    return horizontalGutterGaps[selectedAccordionPropName];
  }
  return verticalGutterGaps[selectedAccordionPropName];
};
const checkHeightChanged = (height, node) => {
  if (!node.changed || node.changed && !node.changed.height) {
    node.properties.height = height;
    node.laptopProperties.height = height;
    node.tabletProperties.height = height;
    node.mobileProperties.height = height;
    if (!node.changed) {
      node.changed = {
        height: true,
        span: false
      };
    } else {
      node.changed.height = true;
    }
  }
};
const checkSpanChanged = (start, span, selectedAccordionPropName, node) => {
  if (!node.changed || node.changed && !node.changed.span) {
    const newProps = findAllSpans(start, span, selectedAccordionPropName);
    Object.assign(node.properties, newProps.properties);
    Object.assign(node.laptopProperties, newProps.laptopProperties);
    Object.assign(node.tabletProperties, newProps.tabletProperties);
    Object.assign(node.mobileProperties, newProps.mobileProperties);
    if (!node.changed) {
      node.changed = {
        height: false,
        span: true
      };
    } else {
      node.changed.span = true;
    }
  }
};
const findAllSpans = (start, span, selectedAccordionPropName) => {
  const reducedSpan = span / completeProperties[selectedAccordionPropName];
  const reducedStart = start / completeProperties[selectedAccordionPropName];
  const boxProperties = {
    properties: {
      span: 0,
      columnStart: 0,
      columnEnd: 0,
      spanStartEnd: ""
    },
    laptopProperties: {
      span: 0,
      columnStart: 0,
      columnEnd: 0,
      spanStartEnd: ""
    },
    tabletProperties: {
      span: 0,
      columnStart: 0,
      columnEnd: 0,
      spanStartEnd: ""
    },
    mobileProperties: {
      span: 0,
      columnStart: 0,
      columnEnd: 0,
      spanStartEnd: ""
    }
  };
  if (selectedAccordionPropName === "properties" || selectedAccordionPropName === "laptopProperties") {
    Object.keys(reversePropertyMapFull).forEach((element) => {
      boxProperties[element].span = Math.round(
        reducedSpan * completeProperties[element]
      );
      boxProperties[element].columnStart = Math.ceil(
        reducedStart * completeProperties[element]
      );
    });
  } else {
    Object.keys(reversePropertyMapFull).forEach((element) => {
      boxProperties[element].span = Math.round(
        reducedSpan * completeProperties[element]
      );
      boxProperties[element].columnStart = start;
    });
  }
  Object.keys(boxProperties).forEach(
    (element) => {
      if (boxProperties[element].span === 0) {
        boxProperties[element].span = 1;
      }
      boxProperties[element].columnEnd = boxProperties[element].columnStart - 1 + boxProperties[element].span;
      boxProperties[element].spanStartEnd = `${boxProperties[element].columnStart}-${boxProperties[element].columnEnd}`;
    }
  );
  return boxProperties;
};
const calculateHeightOfRecursiveChildren = (jsonData, addNewContainer) => {
  let maxDepth = findMaxDepth(jsonData);
  if (maxDepth > 3) {
    maxDepth = maxDepth - 2;
  }
  for (let index = 0; index <= maxDepth; index++) {
    setParentHeight(jsonData, addNewContainer);
  }
};
const findMaxDepth = (obj) => {
  let maxDepth = 0;
  if (obj.children && typeof obj.children !== "string") {
    obj.children.forEach((child) => {
      const childDepth = findMaxDepth(child);
      if (childDepth > maxDepth) {
        maxDepth = childDepth;
      }
    });
  }
  return maxDepth + 1;
};

function cloneDeep(obj) {
  if (obj === null || typeof obj !== "object") {
    return obj;
  }
  if (Array.isArray(obj)) {
    const newArray = [];
    for (let i = 0; i < obj.length; i++) {
      newArray[i] = cloneDeep(obj[i]);
    }
    return newArray;
  }
  const newObj = {};
  for (const key in obj) {
    if (Object.prototype.hasOwnProperty.call(obj, key)) {
      newObj[key] = cloneDeep(obj[key]);
    }
  }
  return newObj;
}
function each(collection, iteratee) {
  if (Array.isArray(collection)) {
    for (const item of collection) {
      iteratee(item);
    }
  } else {
    const keys = Object.keys(collection);
    for (const key of keys) {
      iteratee(collection[key], key);
    }
  }
}
const pickBy = (object, predicate) => {
  if (typeof predicate !== "function") {
    throw new TypeError("Predicate must be a function");
  }
  const result = {};
  for (const key in object) {
    if (Object.prototype.hasOwnProperty.call(object, key) && predicate(object[key], key)) {
      result[key] = object[key];
    }
  }
  return result;
};
const omit = (obj, props) => {
  obj = { ...obj };
  props.forEach((prop) => delete obj[prop]);
  return obj;
};
const omitBy = (obj, check) => {
  obj = { ...obj };
  Object.entries(obj).forEach(
    ([key, value]) => check(value) && delete obj[key]
  );
  return obj;
};
const set = (obj, path, value) => {
  const pathArray = Array.isArray(path) ? path : path.match(/([^[.\]])+/g);
  pathArray?.reduce(
    (acc, key, i) => {
      if (acc[key] === void 0)
        acc[key] = {};
      if (i === pathArray.length - 1)
        acc[key] = value;
      return acc[key];
    },
    obj
  );
};
const get = (obj, path, defValue) => {
  if (!path)
    return void 0;
  const pathArray = Array.isArray(path) ? path : path.match(/([^[.\]])+/g);
  const result = pathArray?.reduce(
    (prevObj, key) => prevObj && prevObj[key],
    obj
  );
  return result === void 0 ? defValue : result;
};
const capitalize = (str) => `${str.charAt(0).toUpperCase()}${str.slice(1).toLowerCase()}`;
const makeClass = (classes) => classes.filter((e) => e).join(" ").trim();
const generateClassList = (props, baseClass, additionalClass) => Object.entries(props).map(([key, value]) => {
  if (typeof value === "boolean" && value) {
    return `${baseClass}__${key}`;
  }
  if (typeof value === "string") {
    return `${baseClass}__${key}-${value}`;
  }
  return "";
}).concat(additionalClass ?? "", baseClass).filter((cls) => !!cls);
const getNumberArray = (start, end) => {
  return Array.from(Array(end - start).keys()).map((n) => start + n);
};
const generateClass = (props, baseClass, additionalClass) => {
  const classList = generateClassList(props, baseClass, additionalClass);
  return makeClass(classList);
};
const DOMExists = !!(WINDOW && WINDOW.document && WINDOW.document.createElement);
const setCSSVar = (obj) => {
  Object.keys(obj).forEach((key) => obj[key] === void 0 && delete obj[key]);
  return obj;
};
const bindRaf = (fn) => {
  let isRunning = false;
  let args = null;
  const run = () => {
    isRunning = false;
    fn(...args);
  };
  return function(...invokingArguments) {
    args = invokingArguments;
    if (isRunning) {
      return;
    }
    isRunning = true;
    requestAnimationFrame(run);
  };
};
const flatten = (lists) => lists.reduce((a, b) => a.concat(b), []);
const inverseObject = (obj) => Object.assign({}, ...Object.entries(obj).map(([a, b]) => ({ [b]: a })));
const isStrEmpty = (str) => str === void 0 || str === null || typeof str !== "string" || !((str?.trim?.() || "").length > 0);
const KEYBOARD = {
  ESCAPE: "Escape",
  ENTER: "Enter",
  ARROWUP: "ArrowUp",
  ARROWDOWN: "ArrowDown",
  ARROWRIGHT: "ArrowRight",
  ARROWLEFT: "ArrowLeft",
  TAB: "Tab"
};
const debounce = (fn, wait, immediate = false) => {
  let timeoutId;
  return function(...args) {
    const later = () => {
      timeoutId = null;
      if (!immediate)
        fn.apply(this, args);
    };
    const callNow = immediate && !timeoutId;
    timeoutId !== null && clearTimeout(timeoutId);
    timeoutId = setTimeout(later, wait);
    if (callNow)
      fn.apply(this, args);
  };
};
function spaceBeforeCapital(str) {
  if (str.length < 1)
    return "";
  return str.charAt(0).toUpperCase() + str.slice(1).replace(/([A-Z])/g, " $1").trim();
}
var ImageRatios = /* @__PURE__ */ ((ImageRatios2) => {
  ImageRatios2["wide"] = "wide";
  ImageRatios2["landscape"] = "landscape";
  ImageRatios2["square"] = "square";
  ImageRatios2["portrait"] = "portrait";
  return ImageRatios2;
})(ImageRatios || {});
const ASPECT_RATIO_TO_PERCENTAGE_MAP = {
  ["wide" /* wide */]: "56.25%",
  ["landscape" /* landscape */]: "75%",
  ["square" /* square */]: " 100%",
  ["portrait" /* portrait */]: "125%"
};
const PositionsList = [
  "top-start",
  "top",
  "top-end",
  "right-start",
  "right",
  "right-end",
  "bottom-start",
  "bottom",
  "bottom-end",
  "left-start",
  "left",
  "left-end"
];
function getModalPosition(anchorRect, modalRect, position = "bottom") {
  let top = 0;
  let left = 0;
  switch (position) {
    case "top":
      top = anchorRect.top - modalRect.height;
      left = anchorRect.left + anchorRect.width / 2 - modalRect.width / 2;
      break;
    case "top-start":
      top = anchorRect.top - modalRect.height;
      left = anchorRect.left;
      break;
    case "top-end":
      top = anchorRect.top - modalRect.height;
      left = anchorRect.left + anchorRect.width - modalRect.width;
      break;
    case "bottom":
      top = anchorRect.top + anchorRect.height;
      left = anchorRect.left + anchorRect.width / 2 - modalRect.width / 2;
      break;
    case "bottom-start":
      top = anchorRect.top + anchorRect.height;
      left = anchorRect.left;
      break;
    case "bottom-end":
      top = anchorRect.top + anchorRect.height;
      left = anchorRect.left + anchorRect.width - modalRect.width;
      break;
    case "left":
      top = anchorRect.top + anchorRect.height / 2 - modalRect.height / 2;
      left = anchorRect.left - modalRect.width;
      break;
    case "left-start":
      top = anchorRect.top;
      left = anchorRect.left - modalRect.width;
      break;
    case "left-end":
      top = anchorRect.top + anchorRect.height - modalRect.height;
      left = anchorRect.left - modalRect.width;
      break;
    case "right":
      top = anchorRect.top + anchorRect.height / 2 - modalRect.height / 2;
      left = anchorRect.left + anchorRect.width;
      break;
    case "right-start":
      top = anchorRect.top;
      left = anchorRect.left + anchorRect.width;
      break;
    case "right-end":
      top = anchorRect.top + anchorRect.height - modalRect.height;
      left = anchorRect.left + anchorRect.width;
      break;
  }
  return {
    top: Math.round(top >= 0 ? top : 0),
    left: Math.round(left >= 0 ? left : 0)
  };
}
function getLineNumbers(domRef) {
  const comComputedStyles = window.getComputedStyle(domRef);
  const divHeight = +domRef.offsetHeight;
  const lineHeight = +comComputedStyles.getPropertyValue("line-height").replace("px", "");
  const lines = divHeight / lineHeight;
  return lines;
}
const assignDefaultValues = (reference, defaultValues) => {
  Object.keys(defaultValues).forEach((prop) => {
    if (reference[prop] === void 0) {
      reference[prop] = defaultValues[prop];
    }
  });
  return reference;
};
const removeBraces = (value) => value.replace(/[{}]/g, "");

function merge(base, ...rest) {
  let second;
  rest = rest.filter(Boolean);
  if (rest.length > 1) {
    const [first, ...others] = rest;
    second = merge(first, ...others);
  } else if (rest.length === 1) {
    second = rest[0];
  } else {
    return base;
  }
  return mergeObject(base, second);
}
function mergeObject(base, second) {
  const merged = {};
  const keys = [...Object.keys(base), ...Object.keys(second)];
  for (const key of keys) {
    if (!(key in second)) {
      merged[key] = base[key];
    } else if (!(key in base)) {
      merged[key] = second[key];
    } else {
      merged[key] = mergeValues(base[key], second[key]);
    }
  }
  return merged;
}
function mergeValues(base, second) {
  if (isObject(base) && isObject(second)) {
    return mergeObject(base, second);
  }
  return second;
}
function isObject(value) {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}
const capitalise = (s) => s.replace(/./, (m) => m.toUpperCase());
const camelCase = (s, separator = "-") => s.split(separator).map((segment, index) => index === 0 ? segment : capitalise(segment)).join("");
const pascalCase = (s, separator = "-") => s.split(separator).map(capitalise).join("");
function performanceWrapper(func, details) {
  const start = performance.now();
  const result = func();
  const end = performance.now();
  setTimeout(() => {
    console.log(
      `Time Taken${details ? ` for ${details}` : ""}: ${end - start}`
    );
  });
  return result;
}
const kebabcase = (inputString) => inputString.replace(/([a-z])([A-Z])/g, "$1-$2").replace(/([0-9])([a-zA-Z])/g, "$1-$2").replace(/([a-zA-Z])([0-9])/g, "$1-$2").replace(/\s+/g, "-").toLowerCase();
function deepClone(obj) {
  return globalThis.structuredClone ? globalThis.structuredClone(obj) : JSON.parse(JSON.stringify(obj));
}

export { ASPECT_RATIO_TO_PERCENTAGE_MAP, DOMExists, Devices, ImageRatios, KEYBOARD, PositionsList, WINDOW, addElementData, assignDefaultValues, bindRaf, boxHasChild, calculateHeightOfRecursiveChildren, camelCase, capitalise, capitalize, changeContent, checkHeightChanged, checkSectionEnabled, checkSpanChanged, cloneDeep, completeProperties, debounce, deepClone, defaultBreakpoints, each, findAllSpans, findBoxWidth, findChild, findGap, findGridHeight, findMaxDepth, findOccupiedSpaces, findOverlappedElement, findParentGrid, flatten, generateClass, generateClassList, get, getConsecutiveSequences, getLineNumbers, getModalPosition, getNewObjectData, getNumberArray, horizontalGutterGaps, inverseObject, isStrEmpty, kebabcase, leftThreshold, makeClass, merge, mergeExistingProperties, moveAllToBack, omit, omitBy, pascalCase, performanceWrapper, pickBy, propertyMap, propertyMapFull, reOrderData, removeBraces, reversePropertyMapFull, rightThreshold, set, setCSSVar, setChildProps, setParentHeight, spaceBeforeCapital, verticalGutterGaps };
